import App from 'App';
// import CanvasPage from 'apps/canvas/canvas-page/CanvasPage';
import CanvasRedirectOldRouteToNewRoute from 'apps/canvas/canvas/canvas-redirect-old-route-to-new-route/CanvasRedirectOldRouteToNewRoute';
import PublicContext, {
	PublicContextValue,
} from 'apps/canvas/canvas/canvas-with-public-context/public-context/PublicContext';
import MarketingBanner from 'core/layouts/components/marketing/marketing-banner/MarketingBanner';
import Layout from 'core/layouts/layout/Layout';
import AnonymousAuth from 'core/navigation/anonymous-auth/AnonymousAuth';
import InitialiseUser from 'core/navigation/initialise-user/InitialiseUser';
import LoadingScreen from 'core/navigation/loading-screen/LoadingScreen';
import RequireAuth from 'core/navigation/require-auth/RequireAuth';
import {
	ACCOUNT,
	API,
	APP,
	BUY_CREDITS,
	CANVAS_ID,
	CANVASES,
	DESKTOP_LOGIN,
	DESKTOP_NEW_TAB,
	HOME,
	LOGIN,
	MY_CHARACTERS,
	PAYMENT_ERROR,
	PAYMENT_SUCCESSFUL,
	PRICING,
	PROFILE,
	PROFILE_HANDLE,
	PUBLIC_CANVAS,
	QUICK_START,
	ROOT,
	STUDIO,
	TEXT_TO_IMAGE,
} from 'core/navigation/Routes';
import SubdomainRedirect from 'core/navigation/subdomain-redirect/SubdomainRedirect';
import DesktopNewTab from 'platform/desktop/DesktopNewTab';
import PopupCallback from 'platform/desktop/popup-callback/PopupCallback';
import React, { lazy, Suspense } from 'react';
import {
	createBrowserRouter,
	createHashRouter,
	createRoutesFromElements,
	Navigate,
	Route,
	RouterProvider,
} from 'react-router-dom';

const AuthPage = lazy(() => import('core/layouts/components/auth-page/AuthPage'));
const StudioTab = lazy(() => import('apps/studio/studio-tab/StudioTab'));
const CanvasPage = lazy(() => import('apps/canvas/canvas-page/CanvasPage'));
const ServerUnderMaintenance = lazy(
	() => import('core/layouts/server-under-maintenance/ServerUnderMaintenance'),
);
const ErrorPage = lazy(() => import('platform/web/error-page/ErrorPage'));
const PaymentErrorPopUp = lazy(
	() =>
		import(
			'platform/web/profile-page/components/payment-pop-ups/payment-result-pop-ups/PaymentErrorPopUp'
		),
);
const PaymentSuccessfulPopUp = lazy(
	() =>
		import(
			'platform/web/profile-page/components/payment-pop-ups/payment-result-pop-ups/PaymentSuccessfulPopUp'
		),
);
const AccountsPage = lazy(() => import('platform/web/accounts-page/AccountsPage'));
const EditProfilePage = lazy(() => import('platform/web/edit-profile-page/EditProfilePage'));
const CanvasListPage = lazy(() => import('apps/canvas/canvas-list-page/CanvasListPage'));
const HomePage = lazy(() => import('platform/web/home-page/HomePage'));
const PricingPage = lazy(() => import('platform/web/pricing-page/PricingPage'));
const MyCharacters = lazy(() => import('platform/web/my-characters/MyCharacters'));
const RenderNetAPI = lazy(() => import('platform/web/api-page/ApiPage'));
const QuickStartPage = lazy(() => import('platform/web/quick-start/QuickStart'));

export default function RouterConfig() {
	const createRouter = window.electron ? createHashRouter : createBrowserRouter;
	const router = createRouter(
		createRoutesFromElements(
			<Route path={ROOT} element={<App />} errorElement={<ErrorPage />}>
				<Route errorElement={<ErrorPage />}>
					<Route element={<SubdomainRedirect isShareMode={false} />}>
						<Route
							path={LOGIN}
							element={
								<Suspense fallback={<LoadingScreen />}>
									<AuthPage />
								</Suspense>
							}
						/>
						<Route
							path={DESKTOP_NEW_TAB}
							element={
								<Suspense fallback={<LoadingScreen />}>
									<DesktopNewTab />
								</Suspense>
							}
						/>
					</Route>
					<Route element={<InitialiseUser />}>
						<Route element={<SubdomainRedirect isShareMode />}>
							<Route
								element={
									<Suspense fallback={<LoadingScreen />}>
										<ServerUnderMaintenance isShareMode />
									</Suspense>
								}
							>
								<Route element={<AnonymousAuth />}>
									<Route element={<Layout />}>
										<Route
											path={`${PUBLIC_CANVAS}/:${CANVAS_ID}?`}
											element={
												<Suspense fallback={<LoadingScreen />}>
													<PublicContext.Provider value={PublicContextValue}>
														<CanvasPage />
													</PublicContext.Provider>
												</Suspense>
											}
										/>
									</Route>
								</Route>
							</Route>
						</Route>
						<Route element={<SubdomainRedirect isShareMode={false} />}>
							<Route
								element={
									<Suspense fallback={<LoadingScreen />}>
										<ServerUnderMaintenance />
									</Suspense>
								}
							>
								<Route path={DESKTOP_LOGIN} element={<PopupCallback />} />
								<Route element={<RequireAuth />}>
									<Route index element={window.electron ? <CanvasListPage /> : <HomePage />} />

									{/* redirect old route to new route */}
									<Route path={HOME} element={<Navigate to={ROOT} replace />} />
									<Route
										path={STUDIO}
										element={<Navigate to={`/${APP}/${TEXT_TO_IMAGE}`} replace />}
									/>
									<Route
										path={TEXT_TO_IMAGE}
										element={<Navigate to={`/${APP}/${TEXT_TO_IMAGE}`} replace />}
									/>
									<Route
										path={CANVASES}
										element={<Navigate to={`/${APP}/${CANVASES}`} replace />}
									/>
									<Route
										path={`${STUDIO}/:${CANVAS_ID}?`}
										element={<CanvasRedirectOldRouteToNewRoute />}
									/>
									{/* redirect old route to new route */}

									<Route path={APP}>
										<Route index element={window.electron ? <CanvasListPage /> : <HomePage />} />
										<Route element={<Layout />}>
											<Route
												path={TEXT_TO_IMAGE}
												element={
													<Suspense fallback={<LoadingScreen />}>
														<StudioTab />
													</Suspense>
												}
											/>
											<Route
												path={`${STUDIO}/:${CANVAS_ID}?`}
												element={
													<Suspense fallback={<LoadingScreen />}>
														<CanvasPage />
													</Suspense>
												}
											/>
											<Route
												path={`draw/:${CANVAS_ID}?`}
												element={
													<Suspense fallback={<LoadingScreen />}>
														<CanvasPage />
													</Suspense>
												}
											/>
										</Route>
										<Route
											path={CANVASES}
											element={
												<Suspense fallback={<LoadingScreen />}>
													<CanvasListPage />
												</Suspense>
											}
										/>
									</Route>

									<Route element={<MarketingBanner />}>
										<Route
											path={ACCOUNT}
											element={
												<Suspense fallback={<LoadingScreen />}>
													<AccountsPage />
												</Suspense>
											}
										>
											<Route path={PAYMENT_SUCCESSFUL} element={<PaymentSuccessfulPopUp />} />
											<Route path={PAYMENT_ERROR} element={<PaymentErrorPopUp />} />
										</Route>
										<Route
											path={`${PROFILE}/:${PROFILE_HANDLE}/edit`}
											element={
												<Suspense fallback={<LoadingScreen />}>
													<EditProfilePage />
												</Suspense>
											}
										/>

										{/* redirect old route to new route */}
										<Route path={BUY_CREDITS} element={<Navigate to={`/${PRICING}`} replace />} />
										<Route
											path={PRICING}
											element={
												<Suspense fallback={<LoadingScreen />}>
													<PricingPage />
												</Suspense>
											}
										/>

										<Route
											path={MY_CHARACTERS}
											element={
												<Suspense fallback={<LoadingScreen />}>
													<MyCharacters />
												</Suspense>
											}
										/>
										<Route
											path={QUICK_START}
											element={
												<Suspense fallback={<LoadingScreen />}>
													<QuickStartPage />
												</Suspense>
											}
										/>
										<Route
											path={API}
											element={
												<Suspense fallback={<LoadingScreen />}>
													<RenderNetAPI />
												</Suspense>
											}
										/>

										<Route
											path="*"
											element={
												<Suspense fallback={<LoadingScreen />}>
													<ErrorPage />
												</Suspense>
											}
										/>
									</Route>
								</Route>
							</Route>
						</Route>
					</Route>
				</Route>
			</Route>,
		),
	);

	return <RouterProvider router={router} />;
}
